import { TicketIcon, Trash2Icon, UserPlusIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { Input } from '@ems/ui/components/input'
import { Field } from '@ems/ui/patterns/field'

import type { BadgeType } from '#admin/features/exhibitor/onboarding/api.ts'
import { DeskSection } from '#admin/features/exhibitor/onboarding/desk-section.tsx'
import { PassTypePicker } from '#admin/features/exhibitor/onboarding/pass-type-picker.tsx'
import type { Settlement, StaffRow } from '#admin/features/exhibitor/onboarding/draft.ts'

/**
 * The staff on the stand, one row for each pass: a convention pass or an exhibitor pass. The stands give the passes, so the
 * rows stop at the quota. The desk issues their badges at once, with a QR the door scans.
 *
 * Only a paid stand has passes (the server refuses a claim before payment). When the desk issues
 * an invoice, the exhibitor names the staff in the portal after the invoice is paid.
 */
export function StaffPassesSection({
  staff,
  onStaff,
  quota,
  badgeTypes,
  passTypeId,
  onPassType,
  settlement,
  issues,
  locked,
}: {
  staff: readonly StaffRow[]
  onStaff: (staff: StaffRow[]) => void
  quota: number
  badgeTypes: readonly BadgeType[]
  passTypeId: string
  onPassType: (id: string) => void
  settlement: Settlement
  issues: Readonly<Record<string, string>>
  locked: boolean
}) {
  const set = (key: string, changes: Partial<StaffRow>) =>
    onStaff(staff.map((row) => (row.key === key ? { ...row, ...changes } : row)))
  const add = () =>
    onStaff([...staff, { key: crypto.randomUUID(), name: '', email: '', title: '' }])

  const blocked =
    badgeTypes.length === 0
      ? 'No free pass type for exhibitors is set up. Add an exhibitor pass in Ticket types, or mark a free type "Exhibitors can get this pass".'
      : settlement === 'invoice'
        ? 'A stand gets its passes when it is paid. After the invoice is paid, the exhibitor names the staff in the portal.'
        : null

  return (
    <DeskSection
      number={4}
      title="Convention and exhibitor passes"
      description={
        blocked === null
          ? `The stands give ${quota} ${quota === 1 ? 'pass' : 'passes'}. Issue them as convention passes (the conference and the exhibition) or as exhibitor passes (the stand crew only). Optional: the exhibitor can name the others in the portal.`
          : undefined
      }
      done={locked}
    >
      {blocked !== null ? (
        <p className="bg-muted/50 text-muted-foreground flex items-start gap-2 rounded-md p-3 text-sm">
          <TicketIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          {blocked}
        </p>
      ) : (
        <>
          <PassTypePicker
            types={badgeTypes}
            value={passTypeId}
            onChange={onPassType}
            error={issues['passType']}
            disabled={locked}
          />

          {staff.length === 0 ? null : (
            <ol className="flex flex-col gap-3">
              {staff.map((row, index) => (
                <li
                  key={row.key}
                  className="grid items-start gap-3 rounded-lg border border-(--border) p-3 md:grid-cols-[1fr_1fr_1fr_auto]"
                >
                  <Field label={`Name ${index + 1}`} error={issues[`staff.${row.key}.name`]}>
                    {(props) => (
                      <Input
                        {...props}
                        value={row.name}
                        maxLength={200}
                        disabled={locked}
                        onChange={(event) => set(row.key, { name: event.target.value })}
                      />
                    )}
                  </Field>
                  <Field label="Email" error={issues[`staff.${row.key}.email`]}>
                    {(props) => (
                      <Input
                        {...props}
                        type="email"
                        value={row.email}
                        maxLength={320}
                        disabled={locked}
                        onChange={(event) => set(row.key, { email: event.target.value })}
                      />
                    )}
                  </Field>
                  <Field label="Badge title" help="Optional. For example, Sales Manager.">
                    {(props) => (
                      <Input
                        {...props}
                        value={row.title}
                        maxLength={200}
                        disabled={locked}
                        onChange={(event) => set(row.key, { title: event.target.value })}
                      />
                    )}
                  </Field>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="md:mt-6"
                    aria-label={`Remove person ${index + 1}`}
                    disabled={locked}
                    onClick={() => onStaff(staff.filter((other) => other.key !== row.key))}
                  >
                    <Trash2Icon />
                  </Button>
                </li>
              ))}
            </ol>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={locked || staff.length >= quota}
              onClick={add}
            >
              <UserPlusIcon />
              Add a person
            </Button>
            <span
              role="status"
              className={
                issues['staff'] === undefined
                  ? 'text-muted-foreground text-sm tabular-nums'
                  : 'text-destructive text-sm'
              }
            >
              {issues['staff'] ??
                (quota === 0
                  ? 'Select a stand to get passes.'
                  : `${staff.length} of ${quota} passes named`)}
            </span>
          </div>
        </>
      )}
    </DeskSection>
  )
}
